import * as React from "react";

import { Box, Link, ListItem, Typography } from "@material-ui/core";

import { MopButton } from "client/components";
import { OnboardingStatusContext } from "client/context";
import {
  useBranding,
  useMerchantModel,
  useOnboardingStatusContext,
  useRunOnce,
  useTranslation,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { ECommercePlatform } from "client/lib/apt-merchant";
import { BrandTitle } from "client/utils/branding";

import { StatusLabel } from "../StatusLabel";
import { StatusStep } from "../StatusStep";
import { useStyles } from "../styles";
import { Status } from "../types";
import type { OnboardingStatusToCurrentStateMap, StatusMap } from "../types";

export const CLEARPAY_SHOPIFY_DOC_URL =
  "https://apps.shopify.com/clearpay-messaging";
export const CLEARPAY_WOOCOMMERCE_DOC_URL =
  " https://developers.clearpay.co.uk/clearpay-online/docs/woo-comm-configure-plugin";
export const CLEARPAY_DEFAULT_PLATFORM_DOC_URL =
  "https://developers.clearpay.co.uk/clearpay-online/docs/messaging-getting-started";
export const AP_SHOPIFY_DOC_URL = "https://apps.shopify.com/afterpay-messaging";
export const AP_WOOCOMMERCE_DOC_URL =
  "https://developers.afterpay.com/afterpay-online/docs/woocommerce-install-plugin";
export const AP_DEFAULT_PLATFORM_DOC_URL =
  "https://developers.afterpay.com/afterpay-online/docs/platforms-quickstart";
const HELP_URL =
  "https://afterpay.formstack.com/forms/global_merchant_technical_support";

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "Declined",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "None",
  REQUIRES_ATTENTION: "Paused",
  READY_TO_INSTALL: "None",
  COMPLETE: "InProgress",
};

type ButtonState = "ShowMeHow" | "MarkAsDone";
type UiState = "Done" | "InProgress" | "None" | "Paused";

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    "messagingComplete" | "onboardingStatus"
  >
) => Status = ({ messagingComplete, onboardingStatus }) =>
  messagingComplete
    ? "Done"
    : onboardingStatusToCurrentStateMap[onboardingStatus];

export const determineDocUrl = (
  platform: ECommercePlatform | undefined,
  brandTitle: BrandTitle = "Afterpay"
): string => {
  if (brandTitle === "Clearpay") {
    if (platform === "Shopify") {
      return CLEARPAY_SHOPIFY_DOC_URL;
    } else if (platform === "WooCommerce") {
      return CLEARPAY_WOOCOMMERCE_DOC_URL;
    }

    return CLEARPAY_DEFAULT_PLATFORM_DOC_URL;
  }

  if (platform === "Shopify") {
    return AP_SHOPIFY_DOC_URL;
  } else if (platform === "WooCommerce") {
    return AP_WOOCOMMERCE_DOC_URL;
  }

  return AP_DEFAULT_PLATFORM_DOC_URL;
};

export const AddMessagingStep: React.FC = () => {
  const { merchant } = useMerchantModel();
  const t = useTranslation();
  const { brandTitle } = useBranding();
  const classes = useStyles();
  const [buttonState, setButtonState] = React.useState<ButtonState>(
    "ShowMeHow"
  );

  const {
    messagingComplete,
    onAddMessagingComplete,
    onboardingStatus,
  } = useOnboardingStatusContext();

  const [currentState, setCurrentState] = React.useState<UiState>(
    determineCurrentState({
      messagingComplete,
      onboardingStatus,
    }) as UiState
  );

  useRunOnce(() => logAmplitudeEvent("STATUS_PAGE", "messaging_card_loaded"));

  React.useEffect(() => {
    setCurrentState(
      determineCurrentState({
        messagingComplete,
        onboardingStatus,
      }) as UiState
    );
  }, [messagingComplete, onboardingStatus]);

  const docUrl = determineDocUrl(
    merchant.storeDetails.platform.value,
    brandTitle
  );

  const statusMap: StatusMap = {
    Done: {
      className: classes.dark,
    },
    InProgress: {
      className: classes.light,
    },
    None: {
      className: classes.light,
    },
    Paused: {
      className: classes.light,
      text: t("uiCompleteAfterComplianceReview"),
    },
    Reviewing: {},
    RequiresAttention: {},
    Declined: {
      className: classes.light,
      text: "Please remove any messaging from your store",
    },
  };

  const { className, text } = statusMap[currentState];

  return (
    <StatusStep
      timeToFade={1000}
      activeOnboardingStep="Messaging"
      stepStatus={currentState}
    >
      <Box
        data-testid="add-messaging-step"
        className={`${className} ${classes.statusWrapper}`}
        mb={2}
        p={2}
      >
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>Add {brandTitle} messaging</strong>
          </Typography>
          <StatusLabel status={currentState} />
        </Box>
        {text && (
          <Box mt={1}>
            <Typography variant="body2">{text}</Typography>
          </Box>
        )}
        {currentState === "InProgress" && (
          <Box mt={1}>
            <Box
              alignItems="center"
              display="flex"
              justifyContent="space-between"
              mb={1}
            >
              <Box>
                <ListItem className={classes.listItem}>
                  {t("uiAfterpayMessagingBenefit1")}
                </ListItem>
                <ListItem className={classes.listItem}>
                  {t("uiAfterpayMessagingBenefit2")}
                </ListItem>
              </Box>
              {buttonState === "ShowMeHow" && (
                <Link href={docUrl} rel="noopener" target="_blank">
                  <MopButton
                    className={classes.shortButton}
                    mopVariant="primary"
                    onClick={() => {
                      setButtonState("MarkAsDone");
                      logAmplitudeEvent(
                        "STATUS_PAGE",
                        "messaging_card_howto_clicked"
                      );
                    }}
                  >
                    {t("uiAddMessagingShowMeHow")}
                  </MopButton>
                </Link>
              )}
              {buttonState === "MarkAsDone" && (
                <Box alignItems="center" display="flex" flexDirection="column">
                  <MopButton
                    className={classes.shortButton}
                    mopVariant="primary"
                    onClick={() => {
                      onAddMessagingComplete();
                      setCurrentState("Done");
                      logAmplitudeEvent(
                        "STATUS_PAGE",
                        "messaging_card_complete_clicked"
                      );
                    }}
                  >
                    {t("uiMarkAsDoneAction")}
                  </MopButton>
                  <Link href={docUrl} rel="noopener" target="_blank">
                    <MopButton
                      className={classes.shortButton}
                      mopVariant="secondary"
                      onClick={() => {
                        logAmplitudeEvent(
                          "STATUS_PAGE",
                          "messaging_card_howto2_clicked"
                        );
                      }}
                    >
                      {t("uiAddMessagingShowMeHowAgain")}
                    </MopButton>
                  </Link>
                </Box>
              )}
            </Box>
            <Link
              className={classes.link}
              href={HELP_URL}
              rel="noopener"
              target="_blank"
              onClick={() => {
                logAmplitudeEvent(
                  "STATUS_PAGE",
                  "messaging_card_technical_support_link_clicked"
                );
              }}
            >
              {t("uiNeedHelp")}
            </Link>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
